import {StyleSheet, TextInput, View} from "react-native";
import {Color} from "@/lib/styles";
import {useSession} from "@/lib/auth/auth";
import {Link, router} from "expo-router";
import ActionButton from "@/lib/components/ActionButton";
import {useRef, useState} from "react";
import {CreateUserRequest, Session} from "@/lib/types";
import Page from "@/lib/components/Page";
import Center from "@/lib/components/Center";
import {fetchBackend} from "@/lib/backend";
import ErrorMessage from "@/lib/components/ErrorMessage";
import Nav from "@/lib/components/Nav";
import Header from "@/lib/components/Header";
import globstyles from "@/lib/globstyles";
import Message from "@/lib/components/Message";
import Radio from "@/lib/components/Radio";
import NavButton from "@/lib/components/NavButton";

export default function LoginPage() {
    const {session, setSession} = useSession();

    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [disclaimer, setDisclaimer] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [busy, setBusy] = useState(false);

    const emailInput = useRef<TextInput>(null);
    const passwordInput = useRef<TextInput>(null);

    async function createUserCallback() {
        if (!disclaimer) {
            setError(new Error("Du må godta vilkårene hvis du vil opprette en bruker."));
            return;
        }
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const request: CreateUserRequest = {username, email, password, timezone};
        setBusy(true);
        fetchBackend<Session>("/account/create", "POST", session, request).then(async (session) => {
            setSession(session);
            router.navigate('/u/account');
        }).catch((error) => {
            setError(error);
        }).finally(() => {
            setBusy(false);
        });
    }

    return (
        <Page>
            <Center width={330} style={{gap: 8}}>
                <Header title={"Opprett brukerprofil"}/>

                <Message text={"Delta sosialt eller skap dine egne eventer."}/>

                <View style={styles.formContainer}>
                    <TextInput style={globstyles.formTextInput}
                               placeholder={"Brukernavn (3–30 tegn)"}
                               placeholderTextColor={Color.FadedText}
                               value={username}
                               onChangeText={(username) => {
                                   setUsername(username);
                                   setError(null);
                               }}
                               autoCapitalize={"none"}
                               autoComplete={"username-new"}
                               inputMode={"text"}
                               returnKeyType={"next"}
                               enablesReturnKeyAutomatically={true}
                               focusable={true}
                               autoFocus={true}
                               keyboardAppearance={"dark"}
                               submitBehavior={"submit"}
                               onSubmitEditing={() => emailInput.current?.focus()}
                    />

                    <TextInput style={globstyles.formTextInput}
                               placeholder={"E-postadresse"}
                               placeholderTextColor={Color.FadedText}
                               value={email}
                               onChangeText={(email) => {
                                   setEmail(email);
                                   setError(null);
                               }}
                               autoComplete={"email"}
                               inputMode={"email"}
                               autoCapitalize={"none"}
                               returnKeyType={"next"}
                               enablesReturnKeyAutomatically={true}
                               ref={emailInput}
                               focusable={true}
                               keyboardAppearance={"dark"}
                               submitBehavior={"submit"}
                               onSubmitEditing={() => passwordInput.current?.focus()}
                    />

                    <TextInput style={globstyles.formTextInput}
                               placeholder={"Lag et passord"}
                               placeholderTextColor={Color.FadedText}
                               textContentType={"password"}
                               secureTextEntry={true}
                               value={password}
                               onChangeText={(password) => {
                                   setPassword(password);
                                   setError(null);
                               }}
                               returnKeyType={"send"}
                               focusable={true}
                               enablesReturnKeyAutomatically={true}
                               ref={passwordInput}
                               keyboardAppearance={"dark"}
                               autoComplete={"new-password"}
                               onSubmitEditing={createUserCallback}
                    />

                    <Radio selected={disclaimer}
                           title={"Jeg samtykker"}
                           description={"Kryss av hvis du samtykker til personvernsvilkår og retningslinjer."}
                           checkbox={true}
                           onPress={() => {
                               setError(null);
                               setDisclaimer(!disclaimer);
                           }}
                    />

                    <NavButton href={`/u/docs`} title={"Les vilkårene"}/>

                    {!!error && (<ErrorMessage error={error}/>)}

                </View>

                <Nav>
                    <ActionButton title={"Opprett min bruker"} busy={busy} color={Color.Green} onPress={createUserCallback}/>
                </Nav>

                <Nav style={{justifyContent: "flex-start", gap: 16}}>
                    <Link style={styles.link} href={`/u/account/login`}>Logg inn</Link>
                    <Link style={styles.link} href={`/u/account/resetpassword`}>Glemt passord?</Link>
                </Nav>

            </Center>
        </Page>
    );
}

export const styles = StyleSheet.create({
    formContainer: {
        width: "100%",
        gap: 8,
        minHeight: 80,
        marginTop: 8,
        marginBottom: 8,
    },
    link: {
        color: Color.PrimaryText,
        textDecorationLine: "underline",
        fontSize: 16,
    },
    disclaimer: {
        color: Color.PrimaryText,
        fontSize: 14,
        lineHeight: 20,
    }
})